/**
 * HistoryPage — per-member mood + sleep history. Shown as a dated list
 * (newest first) with the family's moodEmoji / sleepEmoji pairs and any
 * plan/tags/status/note attached to that day.
 *
 * The whole point is "mum can scroll back to last winter and see what
 * the family's mood actually looked like". It's deliberately not a
 * chart — a chart invites over-analysis. A list, with days that didn't
 * have a check-in skipped, reads like a diary and keeps the tone calm.
 *
 * Tier gate: Free tier is capped server-side at 14 days of history.
 * When the user requests more, the API silently returns 14 and the UI
 * shows a soft "Plus unlocks full history" card under the list with an
 * Upgrade button — never a hard wall; the existing 14 days are always
 * visible so the feature still feels useful on Free.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { moodEmoji, sleepEmoji } from "@/lib/vibe";

const DAY_OPTIONS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "1 year" },
];

function fmtDate(ymd) {
  // ymd is "YYYY-MM-DD" in the family's local timezone; render as
  // "Mon 14 Apr" for readability. We pin it to UTC during formatting so
  // the string always matches the stored date regardless of the
  // viewer's device timezone.
  try {
    const d = new Date(`${ymd}T00:00:00Z`);
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    });
  } catch {
    return ymd;
  }
}

export default function HistoryPage({ members }) {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [history, setHistory] = useState(null);
  const [tier, setTier] = useState(null);

  const member = useMemo(
    () => (members || []).find((m) => m.id === memberId) || null,
    [members, memberId],
  );

  // Fetch the history + current billing tier together — the tier lets
  // us render the right Plus-gate message under the list when relevant.
  useEffect(() => {
    let alive = true;
    if (!memberId) return undefined;
    Promise.all([
      api.history(memberId, days).catch(() => []),
      api.billingStatus().catch(() => ({ tier: "free" })),
    ]).then(([h, s]) => {
      if (!alive) return;
      // Server returns oldest-first; show newest at top so the reader's
      // eye lands on "today" first and can scroll down toward the past.
      setHistory((h || []).slice().reverse());
      setTier(s?.tier || "free");
    });
    return () => {
      alive = false;
    };
  }, [memberId, days]);

  if (!member) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <div className="font-display-italic text-3xl">Member not found</div>
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="ff-btn"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const isPaid = tier && tier !== "free";
  const lockedWindow = !isPaid && days > 14;

  return (
    <div className="min-h-screen bg-[color:var(--bg)]">
      <main className="max-w-2xl mx-auto px-5 md:px-6 py-6 md:py-10">
        <div className="flex items-center justify-between gap-3 mb-8">
          <button
            type="button"
            onClick={() => navigate("/app")}
            className="ff-chip inline-flex items-center gap-2"
            data-testid="history-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="ff-input text-sm"
            data-testid="history-range"
          >
            {DAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-8">
          <div className="label-tiny mb-2">{member.name}'s history</div>
          <h1 className="font-display-italic text-4xl md:text-5xl leading-[0.95] tracking-tight">
            How have you been?
          </h1>
        </div>

        {history === null ? (
          <div className="font-display-italic text-xl text-[color:var(--ink-2)]">
            Loading…
          </div>
        ) : history.length === 0 ? (
          <div
            className="ff-card p-6 text-center"
            data-testid="history-empty"
          >
            <div className="font-display-italic text-2xl mb-2">
              No check-ins yet in this window
            </div>
            <p className="text-[color:var(--ink-2)] text-sm">
              Once {member.name} starts checking in, their moods, sleep
              and quick notes will show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="history-list">
            {history.map((c) => (
              <article
                key={c.id || `${c.date}-${c.member_id}`}
                className="ff-card p-4 md:p-5"
                data-testid={`history-entry-${c.date}`}
              >
                <div className="flex items-baseline justify-between gap-3 mb-1.5">
                  <div className="label-tiny">{fmtDate(c.date)}</div>
                  {c.status ? (
                    <div className="label-tiny text-[color:var(--ink-2)] truncate">
                      {c.status}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-baseline gap-5 flex-wrap">
                  {c.mood != null && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="label-tiny">Mood</span>
                      <span className="font-display text-2xl leading-none tracking-tighter">
                        {c.mood}
                      </span>
                      <span className="text-[10px] text-[color:var(--ink-3)]">
                        /10
                      </span>
                      <span className="text-lg leading-none ml-1">
                        {moodEmoji(c.mood)}
                      </span>
                    </div>
                  )}
                  {c.sleep != null && (
                    <div className="flex items-baseline gap-1.5">
                      <span className="label-tiny">Sleep</span>
                      <span className="font-display text-2xl leading-none tracking-tighter">
                        {c.sleep}
                      </span>
                      <span className="text-[10px] text-[color:var(--ink-3)]">
                        /10
                      </span>
                      <span className="text-lg leading-none ml-1">
                        {sleepEmoji(c.sleep)}
                      </span>
                    </div>
                  )}
                </div>
                {(c.plan || (c.tags && c.tags.length > 0)) && (
                  <div className="mt-2 space-y-1">
                    {c.plan && (
                      <div className="text-sm text-[color:var(--ink-2)] leading-snug">
                        {c.plan}
                      </div>
                    )}
                    {c.tags && c.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {c.tags.map((t, i) => (
                          <span
                            key={`${c.date}-tag-${i}`}
                            className="ff-chip text-[11px]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {c.note && (
                  <div className="mt-2 text-[13px] text-[color:var(--ink-3)] leading-snug italic">
                    {c.note}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {lockedWindow && (
          <div
            className="mt-6 ff-card p-5 flex items-start gap-3"
            data-testid="history-plus-gate"
          >
            <Lock
              className="w-4 h-4 mt-0.5 text-[color:var(--ink-3)] shrink-0"
              strokeWidth={2}
            />
            <div className="flex-1">
              <div className="label-tiny mb-1">
                Full history · Plus and up
              </div>
              <p className="text-[13px] text-[color:var(--ink-2)] leading-snug">
                Free plans show the last 14 days. Upgrade to Plus to scroll
                back through last winter, last school year, or any window
                you want — across every member of your family.
              </p>
              <button
                type="button"
                onClick={() => {
                  try {
                    sessionStorage.setItem("ff_open_billing", "history");
                  } catch {
                    /* ignore */
                  }
                  navigate("/app");
                }}
                className="ff-btn mt-3"
                data-testid="history-upgrade-btn"
              >
                Upgrade to Plus →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
